export type RequestPerformanceStats = {
    endTime?: number;
    error?: unknown;
    method?: string;
    startTime: number;
    status?: number;
    totalTime?: number;
    url: string;
    waterfall?: number;
};

type PerformanceObj = {
    now: () => number;
};

// elapsed time (in milliseconds) to consider next api call in another waterfall
const WATERFALL_THRESHOLD = 70;

/**
 * Builds a full url with query params from a fetch request object
 *
 * With native fetch, the URL already includes all query parameters
 */
const buildFullApiUrl = (request: PerfLoggerRequest): string => {
    return request.url;
};

export type PerfLoggerRequest = {
    method?: string;
    url: string;
};

export type PerfLoggerRequestHooks = {
    onHandleResponse: (request: PerfLoggerRequest, response: { status?: number } | null, error?: unknown) => void;
    onSendRequest: (request: PerfLoggerRequest) => void;
};

type RequestLogger = {
    hooks: PerfLoggerRequestHooks;
    requests: RequestPerformanceStats[];
};

/**
 * Creates the hooks that record one page load's API calls into `requests`.
 * The hooks are not installed anywhere: on the server each render keeps its own through the tracking context, so
 * concurrent renders cannot record into each other's log.
 */
export const createRequestLogger = (performance: PerformanceObj): RequestLogger => {
    const requests: RequestPerformanceStats[] = [];

    return {
        hooks: {
            onHandleResponse: (request, response, error) => {
                const requestUrl = buildFullApiUrl(request);
                const index = requests.findIndex((req) => req.url === requestUrl && req.endTime === undefined);
                if (index === -1) {
                    return;
                }

                const endTime = performance.now();

                requests[index] = {
                    ...requests[index],
                    endTime,
                    error,
                    method: request.method,
                    status: response?.status,
                    totalTime: endTime - requests[index].startTime,
                };
            },
            onSendRequest: (request) => {
                requests.push({
                    startTime: performance.now(),
                    url: buildFullApiUrl(request),
                });
            },
        },
        requests,
    };
};

// Calculate the number of request waterfalls among all api calls made to load page data
const calculateNumRequestWaterfalls = (requests: RequestPerformanceStats[]): number => {
    let numWaterfalls = 0;

    requests.forEach((req, i) => {
        if (requests.length === i + 1) {
            req.waterfall = numWaterfalls;
            return;
        }

        if (i === 0) {
            numWaterfalls++;
            req.waterfall = numWaterfalls;
            return;
        }

        const currentStartTime = req.startTime;
        const nextStartTime = requests[i + 1].startTime;
        req.waterfall = numWaterfalls;

        // assume a network waterfall would mean >= WATERFALL_THRESHOLD (in milliseconds) between requests
        if (currentStartTime + WATERFALL_THRESHOLD <= nextStartTime) {
            numWaterfalls++;
        }
    });

    return numWaterfalls;
};

export type ApiFetchPerformanceStats = {
    numRequests: number;
    numWaterfalls: number;
    requests: RequestPerformanceStats[];
    totalFetchTime: number;
};

export const getStats = (requests: RequestPerformanceStats[]): ApiFetchPerformanceStats => {
    const firstRequestStart = Math.min(...requests.map((req) => req.startTime));
    // A call still in flight when the stats are computed has no endTime; counting it would make the total NaN
    const finishedRequestEnds = requests.flatMap((req) => (req.endTime === undefined ? [] : [req.endTime]));

    const numWaterfalls = calculateNumRequestWaterfalls(requests);
    const totalFetchTime = finishedRequestEnds.length
        ? Number((Math.max(...finishedRequestEnds) - firstRequestStart).toFixed(2))
        : 0;
    const numRequests = requests.length;

    return {
        numRequests,
        numWaterfalls,
        requests,
        totalFetchTime,
    };
};
